import { Store } from '@betgames/bg-state-manager';
import { getValidUrl, parseQuery, StorageRepository } from '@betgames/bg-tools';
import { IAuthParams } from 'store/Auth/interfaces';
import { PersistKey } from 'providers/Persist';
import PartnerFeatures from 'store/AppFeatures/AppFeatures.store';
import { IAppState, IPartnerInitialResponse } from './interfaces';
import AppRepository from './App.repository';

export default class AppStore extends Store<IAppState> {
    constructor(
        private readonly repository: AppRepository,
        readonly persistor: StorageRepository,
        private readonly featureToggle: PartnerFeatures,
    ) {
        super({
            key: 'App',
            default: {
                partnerCode: null,
                logo: null,
                showBalance: false,
                showLogo: false,
                games: [],
                taxSetting: null,
                loadingScreen: null,
                isBetCombinationEnabled: true,
            },
        });

        const partnerCode = this.persistor.getItem(PersistKey.partner);

        if (partnerCode) {
            this.setPartnerCode(partnerCode);
        }
    }

    public setPartnerCode = (partnerCode: string): void => {
        this.persistor.setItem(PersistKey.partner, partnerCode);

        this.update((draft) => {
            draft.partnerCode = partnerCode;
        });
        this.repository.setPartnerCode(partnerCode);

        this.featureToggle.initFeaturesByPartner(partnerCode);
    };

    public configure = (query: string): void => {
        const params = parseQuery<IAuthParams>(query);

        const url = this.persistor.getItem(PersistKey.url);

        const apiUrl = getValidUrl(params.apiUrl || url || GLOBAL_CONFIG.apiUrl || '');

        this.repository.setApiUrl(apiUrl);

        if (!url || apiUrl !== url) {
            this.persistor.setItem(PersistKey.url, apiUrl);
        }

        if (params.partnerCode) {
            this.setPartnerCode(params.partnerCode);
        }
    };

    public fetchInitial = (): Promise<IPartnerInitialResponse> => {
        const startTime = Date?.systemNow() || 0;

        return this.repository.fetch().then((response) => {
            this.update((draft) => {
                draft.games = response.games;
                draft.logo = response.logo;
                draft.showBalance = response.showBalance;
                draft.showLogo = response.showLogo;
                draft.loadingScreen = response.loadingScreen;
                draft.taxSetting = response.taxSetting?.enabled ? response.taxSetting : null;
                draft.isBetCombinationEnabled = response.isBetCombinationEnabled;
            });

            const timeDifference =
                Date.systemNow() - Math.round(response.now + (Date.systemNow() - startTime) / 2);

            // If the difference is less than 1 seconds probably the system time is correct (Should be tested)
            if (Math.abs(timeDifference) > 1000) {
                console.info('Date.now() has changed, time difference:', timeDifference);

                Object.defineProperty(window.Date, 'now', {
                    get: () => () => {
                        return Date.systemNow() - timeDifference;
                    },
                    enumerable: false,
                });
            }

            return response;
        });
    };
}
